import { useCallback, useRef, type TouchEvent } from "react";

export interface UseLongPressOptions {
  /** long press 判定までの時間 (= ms、 default 500)。 */
  delayMs?: number;
  /** touchmove 許容距離 (= px、 default 10)。 これを超えたら長押し判定を取り消す。 */
  moveThreshold?: number;
}

export interface UseLongPressHandlers {
  onTouchStart: (event: TouchEvent<HTMLElement>) => void;
  onTouchEnd: (event: TouchEvent<HTMLElement>) => void;
  onTouchMove: (event: TouchEvent<HTMLElement>) => void;
  onTouchCancel: (event: TouchEvent<HTMLElement>) => void;
}

/**
 * touch long-press gesture を delay + move threshold で検出する hook。
 * touchstart で timer 開始、 delayMs 経過で callback 発火。
 * 途中で touchmove (= moveThreshold 超過) / touchend / touchcancel が来たら判定取消。
 *
 * mobile-pwa Phase 4.3 で SceneView に配線、 long-press で通報 modal を起動する用途を想定。
 * tap (= 短押し) や swipe (= 移動) と排他、 useSwipeGesture と併用可能。
 *
 * 使用例:
 *   const handlers = useLongPress(() => openReportModal(), { delayMs: 600 });
 *   return <div {...handlers}>scene 本文</div>;
 */
export function useLongPress(
  callback: () => void,
  options: UseLongPressOptions = {},
): UseLongPressHandlers {
  const { delayMs = 500, moveThreshold = 10 } = options;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startRef.current = null;
  }, []);

  const onTouchStart = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      const touch = event.touches[0];
      if (touch === undefined) return;
      startRef.current = { x: touch.clientX, y: touch.clientY };
      timerRef.current = setTimeout(() => {
        callback();
        timerRef.current = null;
        startRef.current = null;
      }, delayMs);
    },
    [callback, delayMs],
  );

  const onTouchMove = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      const start = startRef.current;
      if (start === null) return;
      const touch = event.touches[0];
      if (touch === undefined) return;
      const dx = Math.abs(touch.clientX - start.x);
      const dy = Math.abs(touch.clientY - start.y);
      if (dx > moveThreshold || dy > moveThreshold) clearTimer();
    },
    [clearTimer, moveThreshold],
  );

  return {
    onTouchStart,
    onTouchEnd: clearTimer,
    onTouchMove,
    onTouchCancel: clearTimer,
  };
}
